import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, PaperPlaneRight, Trash, ChatCircle, PushPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Reply = ({ post, r, onDeleted }) => (
  <div className="flex items-start gap-2 pl-4 border-l-2 border-[#ECEDEF] py-1.5">
    <div className="min-w-0 flex-1">
      <span className="text-xs font-bold text-[#0A6B7A]">{r.alias}{r.is_you ? " (you)" : ""}</span>
      {r.real_name && <span className="text-[10px] text-[#000000] ml-1.5" title="Visible to admins only">· {r.real_name}</span>}
      <span className="text-[10px] text-[#000000] ml-1.5">{ago(r.created_at)}</span>
      <p className="text-sm text-[#27272A] whitespace-pre-wrap break-words">{r.text}</p>
    </div>
    {r.can_delete && (
      <button className="btn-ghost p-1 text-[#B91C1C]" data-testid={`reply-delete-${r.id}`}
        onClick={async () => { await api.delete(`/community/posts/${post.id}/replies/${r.id}`); onDeleted(); }}>
        <Trash size={13} />
      </button>
    )}
  </div>
);

const Post = ({ p, onChanged }) => {
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/community/posts/${p.id}/replies`, { text: reply.trim() }); setReply(""); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className={`card mb-3 ${p.pinned ? "border-2 border-[#CCFF00] relative" : ""}`} data-testid={`community-post-${p.id}`}>
      {p.pinned && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#CCFF00] text-[#000000] mb-1.5" data-testid={`post-pinned-chip-${p.id}`}>
          <PushPin size={11} weight="fill" /> Pinned
        </span>
      )}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <span className="text-sm font-extrabold text-[#0A6B7A]">{p.alias}{p.is_you ? " (you)" : ""}</span>
          {p.real_name && <span className="text-[10px] text-[#000000] ml-1.5" title="Visible to admins only">· {p.real_name}</span>}
          <span className="text-[11px] text-[#000000] ml-2">{ago(p.created_at)}</span>
          <p className="text-[15px] text-[#18181B] mt-1 whitespace-pre-wrap break-words">{p.text}</p>
        </div>
        {p.can_pin && (
          <button className={`btn-ghost p-1.5 ${p.pinned ? "text-[#000000]" : "text-[#000000]"}`} data-testid={`post-pin-${p.id}`}
            title={p.pinned ? "Unpin from top" : "Pin to top of feed"}
            onClick={async () => {
              try {
                const { data } = await api.post(`/community/posts/${p.id}/pin`);
                toast.success(data.pinned ? "Pinned to the top of the feed" : "Post unpinned");
                onChanged();
              } catch (e) { toast.error(formatApiError(e)); }
            }}>
            <PushPin size={15} weight={p.pinned ? "fill" : "regular"} />
          </button>
        )}
        {p.can_delete && (
          <button className="btn-ghost p-1.5 text-[#B91C1C]" data-testid={`post-delete-${p.id}`}
            onClick={async () => { if (window.confirm("Delete this post?")) { await api.delete(`/community/posts/${p.id}`); onChanged(); } }}>
            <Trash size={15} />
          </button>
        )}
      </div>
      <div className="flex items-center gap-4 mt-2">
        <button data-testid={`post-heart-${p.id}`}
          className={`flex items-center gap-1 text-sm bg-transparent border-0 cursor-pointer ${p.hearted_by_you ? "text-[#DC2626]" : "text-[#000000]"}`}
          onClick={async () => { await api.post(`/community/posts/${p.id}/heart`); onChanged(); }}>
          <Heart size={16} weight={p.hearted_by_you ? "fill" : "regular"} /> {p.heart_count || ""}
        </button>
        <button className="flex items-center gap-1 text-sm text-[#000000] bg-transparent border-0 cursor-pointer"
          data-testid={`post-replies-toggle-${p.id}`} onClick={() => setOpen((v) => !v)}>
          <ChatCircle size={16} /> {p.replies.length ? `${p.replies.length} repl${p.replies.length === 1 ? "y" : "ies"}` : "Reply"}
        </button>
      </div>
      {open && (
        <div className="mt-2">
          {p.replies.map((r) => <Reply key={r.id} post={p} r={r} onDeleted={onChanged} />)}
          <div className="flex gap-2 mt-2">
            <input className="input-base flex-1 text-sm" placeholder="Write a supportive reply…" value={reply}
              data-testid={`reply-input-${p.id}`}
              onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} />
            <button className="btn-secondary" onClick={sendReply} data-testid={`reply-send-${p.id}`}><PaperPlaneRight size={15} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CommunityPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRules, setShowRules] = useState(() => {
    try { return !localStorage.getItem("sa_community_rules_seen"); } catch { return true; }
  });
  const [closed, setClosed] = useState(false);
  const load = () => api.get("/community/posts").then(({ data }) => setPosts(data)).catch((e) => {
    if (e?.response?.status === 403) setClosed(true);
    setPosts([]);
  });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post("/community/posts", { text: text.trim() }); setText(""); load(); toast.success("Posted anonymously"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] pb-16" data-testid="community-page">
      {closed && (
        <div className="max-w-lg mx-auto px-4 pt-16 text-center" data-testid="community-closed-notice">
          <div className="text-5xl mb-3">🚧</div>
          <h1 className="text-2xl font-extrabold font-display text-[#000000]">The Community launches soon!</h1>
          <p className="text-[#000000] mt-2">We're getting the space ready. Keep learning — you'll be first in when it opens.</p>
          <button className="btn-primary mt-5" onClick={() => nav("/dashboard")} data-testid="community-closed-back">← Back to dashboard</button>
        </div>
      )}
      {!closed && showRules && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="community-rules-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-extrabold text-[#000000] mb-3">Welcome to the Community 👋</h2>
            <p className="text-sm text-[#000000] mb-3">This is an anonymous space — a few simple rules keep it safe for everyone:</p>
            <ul className="text-sm text-[#27272A] space-y-2 mb-4 list-disc pl-5">
              <li><b>Be kind.</b> Cheer people on — no put-downs, teasing or bullying.</li>
              <li><b>Stay anonymous.</b> Don't share your real name, school, or anyone else's.</li>
              <li><b>What's shared here, stays here.</b> Don't screenshot or repost.</li>
              <li><b>Ask for help when it's serious.</b> If someone is in danger, tell a trusted adult — this space isn't for emergencies.</li>
            </ul>
            <p className="text-xs text-[#000000] mb-4">Admins moderate every post and can see who posted if a rule is broken.</p>
            <button className="btn-primary w-full" data-testid="community-rules-agree"
              onClick={() => { try { localStorage.setItem("sa_community_rules_seen", "1"); } catch {} setShowRules(false); }}>
              I'll be kind — let me in
            </button>
          </div>
        </div>
      )}
      {!closed && (
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <button className="btn-ghost text-sm flex items-center gap-1.5 mb-4" onClick={() => nav("/dashboard")} data-testid="community-back">
          <ArrowLeft size={15} /> Dashboard
        </button>
        <h1 className="text-2xl font-extrabold text-[#000000] mb-1">Community</h1>
        <p className="text-sm text-[#000000] mb-5">Share wins, ask questions, cheer each other on. Posts are <b>anonymous</b> — you appear as a nickname{user ? <> (yours is generated automatically)</> : null}. Be kind; admins moderate.</p>
        <div className="card mb-6">
          <textarea className="input-base w-full text-sm" rows={3} maxLength={2000} value={text}
            data-testid="community-composer"
            placeholder="Share a win or ask the group a question…" onChange={(e) => setText(e.target.value)} />
          <div className="flex justify-end mt-2">
            <button className="btn-primary text-sm" disabled={busy || !text.trim()} onClick={submit} data-testid="community-post-submit-btn">
              {busy ? "Posting…" : "Post anonymously"}
            </button>
          </div>
        </div>
        {posts === null && <p className="text-sm text-[#000000]">Loading…</p>}
        {posts?.length === 0 && <p className="text-sm text-[#000000]" data-testid="community-empty">No posts yet — be the first to share a win!</p>}
        {(posts || []).map((p) => <Post key={p.id} p={p} onChanged={load} />)}
      </div>
      )}
    </div>
  );
}
